import instanceAxios from '../config/api'
import { Action, ActionResponse, ActionType } from '../types/actionTypes'
import { ExistingControl } from '../types/existingControlTypes'
import { SubDomain } from '../types/subDomainTypes'

const API_URL = '/actions'

export const getAllActions = async ({
  page,
  pageSize,
  search,
}: {
  page?: number
  pageSize?: number
  search?: string
}) => {
  try {
    const response = await instanceAxios.get<ActionResponse>(`${API_URL}`, {
      params: {
        search,
        page,
        pageSize,
      },
    })
    return response.data
  } catch (error) {
    console.error('Error fetching actions:', error)
    throw error
  }
}
export const deleteActionApi = async (ActionId: string) => {
  try {
    const response = await instanceAxios.delete<Action[]>(
      `${API_URL}/` + ActionId
    )
    return response.data
  } catch (error) {
    console.error('Error deleting action:', error)
    throw error
  }
}

export const addActionApi = async (newAction: Action): Promise<Action> => {
  try {
    const hasFile = newAction.Support instanceof File

    if (hasFile) {
      const formData = new FormData()

      Object.entries(newAction).forEach(([key, value]) => {
        if (value === undefined || value === null) return

        if (key === 'Support' && value instanceof File) {
          formData.append('Support', value)
          return
        }

        if (value instanceof Date) {
          formData.append(key, value.toISOString())
          return
        }

        if (Array.isArray(value)) {
          value.forEach((item) => formData.append(`${key}[]`, item))
          return
        }

        formData.append(key, value as any)
      })

      const response = await instanceAxios.post<Action>(
        `${API_URL}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      )
      return response.data
    }

    const response = await instanceAxios.post<Action>(`${API_URL}`, newAction)
    return response.data
  } catch (error) {
    console.error('Error adding action:', error)
    throw error
  }
}

export const getActionbyIdApi = async (ActionId: string) => {
  try {
    const response = await instanceAxios.get<Action>(`${API_URL}/` + ActionId)
    return response.data
  } catch (error) {
    // Pass through the original error so handleError can extract the backend message
    console.error('Error fetching action by ID:', error)
    throw error
  }
}

export const updateActionApi = async (
  updateAction: Action,
  _id: string
): Promise<Action> => {
  try {
    const hasFile = updateAction.Support instanceof File

    if (hasFile) {
      const formData = new FormData()

      Object.entries(updateAction).forEach(([key, value]) => {
        if (value === undefined || value === null) return

        if (key === 'Support' && value instanceof File) {
          formData.append('Support', value)
          return
        }

        if (value instanceof Date) {
          formData.append(key, value.toISOString())
          return
        }

        if (Array.isArray(value)) {
          value.forEach((item) => formData.append(`${key}[]`, item))
          return
        }

        formData.append(key, value as any)
      })

      const response = await instanceAxios.put<Action>(
        `${API_URL}/${_id}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      )
      return response.data
    }

    const response = await instanceAxios.put<Action>(
      `${API_URL}/${_id}`,
      updateAction
    )
    return response.data
  } catch (error) {
    console.error('Error updating action:', error)
    throw error
  }
}

export const getActionbyAuditApi = async (AuditId: string) => {
  try {
    const response = await instanceAxios.get<Action[]>(
      `audits/getActions/` + AuditId
    )
    return response.data
  } catch (error) {
    console.error('Error fetching actions by audit:', error)
    throw error
  }
}

export const getActionbyIncidentApi = async (IncidentId: string) => {
  try {
    const response = await instanceAxios.get<Action[]>(
      `incidents/getActions/` + IncidentId
    )
    return response.data
  } catch (error) {
    console.error('Error fetching actions by incident:', error)
    throw error
  }
}

export const getActionbyProjectApi = async (ProjectId: string) => {
  try {
    const response = await instanceAxios.get<ActionResponse>(
      `projets/getActions/` + ProjectId
    )
    return response.data
  } catch (error) {
    console.error('Error fetching actions by project:', error)
    throw error
  }
}

export const getActionbySecurityCheckFamilyApi = async (
  securityControlsId: string
) => {
  try {
    const response = await instanceAxios.get<Action[]>(
      `securityControls/getActions/` + securityControlsId
    )
    return response.data
  } catch (error) {
    console.error('Error fetching actions by security check family:', error)
    throw error
  }
}

export const getActionbyRisqueApi = async (RisqueId: string) => {
  try {
    const response = await instanceAxios.get<Action[]>(
      `risques/getActions/` + RisqueId
    )
    return response.data
  } catch (error) {
    console.error('Error fetching actions by risk:', error)
    throw error
  }
}

export const addActionToProjetApi = async (
  ActionId: string[],
  _id: string
): Promise<ActionResponse> => {
  try {
    const response = await instanceAxios.put<ActionResponse>(
      `projets/addActions/${_id}`,
      ActionId
    )
    return response.data
  } catch (error) {
    console.error('Error adding actions to project:', error)
    throw error
  }
}

export const removeActionFromProjetApi = async (
  actions: ActionType,
  _id: string
): Promise<ActionResponse> => {
  try {
    const response = await instanceAxios.put<ActionResponse>(
      `projets/removeActions/${_id}`,
      actions
    )
    return response.data
  } catch (error) {
    console.error('Error removing action from project:', error)
    throw error
  }
}

export const getActionsByMissionIdApi = async (
  MissionId: string,
  page?: number,
  pageSize?: number
) => {
  try {
    const response = await instanceAxios.get<ActionResponse>(
      `missions/getActions/${MissionId}?page=${page || 1}&pageSize=${pageSize || 10}`
    )
    return response.data
  } catch (error) {
    console.error('Error fetching actions by mission:', error)
    throw error
  }
}

export const getActionsBySubDomainIdApi = async (SubDomainId: string) => {
  try {
    const response = await instanceAxios.get<Action[]>(
      `/subDomains/getActions/${SubDomainId}`
    )
    return response.data
  } catch (error) {
    console.error('Error fetching actions by subdomain:', error)
    throw error
  }
}

export const getExistingControlbySecurityControlyApi = async (
  securityControlsId: string
) => {
  try {
    const response = await instanceAxios.get<ExistingControl[]>(
      `securityControls/getExistingControls/` + securityControlsId
    )
    return response.data
  } catch (error) {
    console.error(
      'Error fetching existing controls by security control:',
      error
    )
    throw error
  }
}

export const getSubDomainbySecurityControlyApi = async (
  securityControlsId: string
) => {
  try {
    const response = await instanceAxios.get<SubDomain[]>(
      `securityControls/getSubDomains/` + securityControlsId
    )
    return response.data
  } catch (error) {
    console.error('Error fetching subdomains by security control:', error)
    throw error
  }
}
